import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { kitEnv, SITE_MODE } from '../data/kitEnv';
import { isPg, subscribeRow } from '../data/supabaseClient';
import { db } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { getPreset } from './presets';
import { useLegalLinks } from '../components/Login';
import './site.css';

// The starter-site chassis. One generic section renderer driven entirely by
// the site document (config row 'site', seeded from templates/presets.js at
// provision) — so every app type is data, not a fork, and operators edit the
// live site without a deploy. Anonymous visitors are the audience: the
// config 'site' row is public-read on both data planes.

export const APP_TYPE = kitEnv('APP_TYPE', process.env.REACT_APP_APP_TYPE || '');
export const TEMPLATE_ACTIVE = !!APP_TYPE && APP_TYPE !== 'blank';

// ---- site document -----------------------------------------------------------------
// Preset defaults render instantly; the seeded/edited row overrides when it
// arrives. Firebase plane falls back to a one-shot doc read.
function useSite() {
  const preset = useMemo(() => getPreset(APP_TYPE), []);
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    if (!TEMPLATE_ACTIVE) return undefined;
    if (SITE_MODE) {
      // Static snapshot: the plane wrote the site row beside the bundle.
      let gone = false;
      fetch('/site.json').then((r) => (r.ok ? r.json() : null)).then((doc) => { if (!gone) setRemote(doc || null); }).catch(() => {});
      return () => { gone = true; };
    }
    if (isPg()) return subscribeRow('config', 'site', (doc) => setRemote(doc || null));
    if (!db) return undefined;
    const unsub = db.doc('config/site').onSnapshot(
      (snap) => setRemote(snap.exists ? snap.data() : null),
      () => setRemote(null)
    );
    return () => unsub();
  }, []);
  // The remote row replaces the preset wholesale (it was seeded FROM the
  // preset) — merging per-field would resurrect deleted demo content.
  return remote && remote.sections ? remote : preset;
}

const cover = (hue = 200) => ({
  background: `linear-gradient(135deg, hsl(${hue}, 70%, 22%), hsl(${(hue + 40) % 360}, 80%, 38%) 55%, hsl(${(hue + 80) % 360}, 75%, 30%))`,
});

// ---- sections ----------------------------------------------------------------------
// One slide of the hero media loop. Videos are muted/inline, play from the
// start when their turn comes and hand off on `ended`; a lone video loops.
// Safety net: if playback is refused (power saving, background tab, iOS
// low-power) the poster holds for one image interval; if `ended` never
// fires (Chromium pauses background video) a duration-based timer advances.
function HeroVideo({ slide, active, solo, interval, onDone }) {
  const ref = React.useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    if (!active) { v.pause(); return undefined; }
    let t;
    const hold = (ms) => { clearTimeout(t); t = setTimeout(onDone, ms); };
    v.currentTime = 0;
    hold(((Number.isFinite(v.duration) && v.duration) || 15) * 1000 + 2500);
    v.play().catch(() => hold(interval));
    return () => clearTimeout(t);
  }, [active, onDone, interval]);
  return (
    <video ref={ref} className={`ks-hero-img ${active ? 'is-on' : ''}`} src={slide.video} poster={slide.poster}
      muted playsInline preload="auto" loop={solo} onEnded={solo ? undefined : onDone} onError={onDone} />
  );
}

// `hero.media` = ordered slides [{image, alt?, position?} | {video, poster?}]
// crossfading in a loop; image slides hold `hero.interval` ms (default 4500).
// A plain `hero.image` is a one-slide loop.
function HeroMedia({ hero }) {
  const slides = useMemo(
    () => (Array.isArray(hero.media) && hero.media.length ? hero.media : [{ image: hero.image, alt: hero.alt }]),
    [hero.media, hero.image, hero.alt]
  );
  const [i, setI] = useState(0);
  const cur = slides[i] || slides[0];
  const interval = hero.interval || 4500;
  const next = React.useCallback(() => setI((n) => (n + 1) % slides.length), [slides.length]);
  useEffect(() => {
    if (slides.length < 2 || cur.video) return undefined;
    const t = setTimeout(next, interval);
    return () => clearTimeout(t);
  }, [i, slides.length, cur.video, interval, next]);
  return (
    <div className="ks-hero-media">
      {slides.map((s, k) => s.video
        ? <HeroVideo key={s.video} slide={s} active={k === i} solo={slides.length === 1} interval={interval} onDone={next} />
        : <img key={s.image} className={`ks-hero-img ${k === i ? 'is-on' : ''}`} src={s.image} alt={s.alt || ''}
            style={s.position ? { objectPosition: s.position } : undefined} />)}
    </div>
  );
}

function Hero({ hero, accent }) {
  if (!hero) return null;
  // `hero.image` / `hero.media` = full-bleed editorial photography (with a
  // scrim); otherwise the generated art backdrop (`hero.art`).
  const photo = hero.image || (hero.media && hero.media.length);
  return (
    <header className={`ks-hero ${photo ? 'ks-hero-image' : `ks-art-${hero.art || 'a'}`}`}>
      {photo && <HeroMedia hero={hero} />}
      <div className="ks-wrap">
        {hero.kicker && <p className="ks-kicker" style={{ color: accent }}>{hero.kicker}</p>}
        <h1>{hero.title}</h1>
        {hero.sub && <p className="ks-sub">{hero.sub}</p>}
        {hero.cta && (
          <a className="ks-btn ks-btn-primary" href={hero.cta.href} style={{ background: accent }}>
            {hero.cta.label}
          </a>
        )}
      </div>
    </header>
  );
}

function Features({ s }) {
  return (
    <div className="ks-grid ks-grid-features">
      {(s.items || []).map((f) => (
        <div key={f.title} className="ks-card">
          <span className="ks-icon">{f.icon}</span>
          <h3>{f.title}</h3>
          <p>{f.text}</p>
        </div>
      ))}
    </div>
  );
}

function Tiers({ s, accent }) {
  return (
    <div className="ks-grid ks-grid-tiers">
      {(s.items || []).map((t) => (
        <div key={t.name} className="ks-card ks-tier">
          <h3>{t.name}</h3>
          <p className="ks-price">{t.price}<span>{t.per}</span></p>
          <p className="ks-muted">{t.blurb}</p>
          <ul>{(t.bullets || []).map((b) => <li key={b}>{b}</li>)}</ul>
          {t.cta && <a className="ks-btn" href={t.cta.href} style={{ borderColor: accent, color: accent }}>{t.cta.label}</a>}
        </div>
      ))}
    </div>
  );
}

function Cards({ s, items, accent }) {
  const list = items || [];
  if (list.length === 0) return <p className="ks-muted">Nothing published yet.</p>;
  return (
    <div className={`ks-grid ks-grid-cards ks-style-${s.style || 'post'}`}>
      {list.map((it) => (
        <Link key={it.id} to={`/p/${it.id}`} className="ks-card ks-item">
          <div className="ks-cover" style={it.image ? undefined : cover(it.hue)}>
            {it.image && <img className="ks-cover-img" src={it.image} alt={it.alt || it.title} loading="lazy" />}
            {it.tag && <span className="ks-tag" style={{ background: accent }}>{it.tag}</span>}
          </div>
          <div className="ks-item-body">
            <h3>{it.title}</h3>
            {it.meta && <p className="ks-meta">{it.meta}</p>}
            {it.excerpt && <p className="ks-muted">{it.excerpt}</p>}
            {it.price && <p className="ks-item-price">{it.price}</p>}
          </div>
        </Link>
      ))}
    </div>
  );
}

function Team({ s, accent }) {
  return (
    <div className="ks-grid ks-grid-team">
      {(s.items || []).map((m) => (
        <div key={m.name} className="ks-card ks-person">
          <span className="ks-avatar" style={{ background: accent }}>{m.initials}</span>
          <h3>{m.name}</h3>
          <p className="ks-meta">{m.role}</p>
          <p className="ks-muted">{m.bio}</p>
        </div>
      ))}
    </div>
  );
}

function Quotes({ s }) {
  return (
    <div className="ks-grid ks-grid-quotes">
      {(s.items || []).map((q) => (
        <blockquote key={q.quote} className="ks-card ks-quote">
          <p>“{q.quote}”</p>
          <footer>{q.name}{q.role ? ` · ${q.role}` : ''}</footer>
        </blockquote>
      ))}
    </div>
  );
}

function Stats({ s, accent }) {
  return (
    <div className="ks-stats">
      {(s.items || []).map((x) => (
        <div key={x.label}>
          <strong style={{ color: accent }}>{x.value}</strong>
          <span>{x.label}</span>
        </div>
      ))}
    </div>
  );
}

function Cta({ s, accent }) {
  return (
    <div className="ks-cta">
      <h2>{s.title}</h2>
      {s.sub && <p className="ks-muted">{s.sub}</p>}
      {s.button && <a className="ks-btn ks-btn-primary" href={s.button.href} style={{ background: accent }}>{s.button.label}</a>}
    </div>
  );
}

function Faq({ items }) {
  return (
    <div className="ks-faq">
      {(items || []).map((f, i) => (
        <details key={i} className="ks-faq-item">
          <summary>{f.q}</summary>
          <p className="ks-muted">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

// Editorial image grid: items { image, alt, caption, href?, span: 'full'|'half' }.
// Full-span items go edge to edge of the wrap; halves pair up.
function Gallery({ s }) {
  return (
    <div className="ks-gallery">
      {(s.items || []).map((g, i) => {
        const inner = (
          <>
            <img src={g.image} alt={g.alt || g.caption || ''} loading="lazy" />
            {g.caption && <figcaption>{g.caption}</figcaption>}
          </>
        );
        const cls = `ks-gallery-item ks-span-${g.span === 'full' ? 'full' : 'half'}`;
        return g.href
          ? <a key={g.image || i} href={g.href} className={cls}>{inner}</a>
          : <figure key={g.image || i} className={cls}>{inner}</figure>;
      })}
    </div>
  );
}

const SECTION_KINDS = {
  gallery: Gallery,
  features: Features,
  faq: Faq,
  tiers: Tiers,
  cards: Cards,
  team: Team,
  quotes: Quotes,
  stats: Stats,
  cta: Cta,
  text: ({ s }) => (
    <div className="ks-prose">
      {String(s.body || '').split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
    </div>
  ),
};

// ---- chassis -----------------------------------------------------------------------
function SiteNav({ site }) {
  const { user } = useAuth();
  const member = user && !user.isAnonymous;
  return (
    <nav className="ks-nav">
      <div className="ks-wrap ks-nav-row">
        {/* Three slots so the editorial layout can centre the wordmark
            (links · brand · sign-in); the default layout orders brand first. */}
        <div className="ks-links">
          {(site.nav || []).map((l) => <a key={l.label} href={l.href}>{l.label}</a>)}
        </div>
        <Link to={member ? "/?site=1" : "/"} className="ks-brand">{site.brand}</Link>
        {/* Site mode has no app behind the site: no sign-in link to a dead door. */}
        {!SITE_MODE && <Link to={member ? '/app' : '/login'} className="ks-signin">{member ? 'Open app' : 'Sign in'}</Link>}
      </div>
    </nav>
  );
}

function SiteFooter({ site }) {
  const legal = useLegalLinks();
  const links = legal ? [['Terms', legal.terms], ['Privacy', legal.privacy], ['End user agreement', legal.eula]].filter(([, href]) => href) : [];
  return (
    <footer className="ks-footer">
      <div className="ks-wrap">
        <span>{site.footer?.text || site.brand}</span>
        <span className="ks-muted"> · © {new Date().getFullYear()}</span>
        {links.map(([label, href]) => (
          <span key={label} className="ks-muted"> · <a href={href} target="_blank" rel="noopener noreferrer">{label}</a></span>
        ))}
      </div>
    </footer>
  );
}

export function TemplateHome() {
  const site = useSite();
  if (!site) return null;
  const accent = site.accent || 'var(--surf)';
  return (
    <div className={`ks-site${site.layout ? ` ks-layout-${site.layout}` : ''}`} style={{ '--ks-accent': accent }}>
      <SiteNav site={site} />
      <Hero hero={site.hero} accent={accent} />
      {(site.sections || []).map((s, i) => {
        const Kind = SECTION_KINDS[s.kind];
        if (!Kind) return null;
        return (
          <section key={s.id || `${s.kind}-${i}`} id={s.id} className="ks-section">
            <div className="ks-wrap">
              {s.title && s.kind !== 'cta' && <h2 className="ks-section-title">{s.title}</h2>}
              {s.sub && <p className="ks-muted ks-section-sub">{s.sub}</p>}
              <Kind s={s} items={s.itemsFrom ? site[s.itemsFrom] : s.items} accent={accent} />
            </div>
          </section>
        );
      })}
      <SiteFooter site={site} />
    </div>
  );
}

export function TemplateDetail() {
  const { id } = useParams();
  const site = useSite();
  if (!site) return null;
  const accent = site.accent || 'var(--surf)';
  const item = (site.items || []).find((x) => x.id === id);
  return (
    <div className={`ks-site${site.layout ? ` ks-layout-${site.layout}` : ''}`} style={{ '--ks-accent': accent }}>
      <SiteNav site={site} />
      <article className="ks-detail">
        <div className="ks-wrap">
          {!item ? (
            <>
              <h1>Not found</h1>
              <p className="ks-muted">This page doesn&apos;t exist (or was unpublished). <Link to="/?site=1">Back to the site</Link>.</p>
            </>
          ) : (
            <>
              <div className="ks-cover ks-cover-wide" style={item.image ? undefined : cover(item.hue)}>
                {item.image && <img className="ks-cover-img" src={item.image} alt={item.alt || item.title} />}
                {item.tag && <span className="ks-tag" style={{ background: accent }}>{item.tag}</span>}
              </div>
              <h1>{item.title}</h1>
              <p className="ks-meta">
                {[item.meta, item.price].filter(Boolean).join(' · ')}
              </p>
              <div className="ks-prose">
                {String(item.body || item.excerpt || '').split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
              </div>
              {item.kind === 'product' && (
                <a className="ks-btn ks-btn-primary" href="/login" style={{ background: accent }}>
                  Order inquiry
                </a>
              )}
              <p style={{ marginTop: 24 }}><Link to="/?site=1" className="ks-back">← {site.brand}</Link></p>
            </>
          )}
        </div>
      </article>
      <SiteFooter site={site} />
    </div>
  );
}
